import { useCallback, type CSSProperties } from "react";
import { ArrowRight, Info, Lock, Eye, Building2 } from "lucide-react";
import { HowItWorksSection } from "./HowItWorksSection";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import illustrationSrc from "../../../../assets/illustrations/accounting-overview.svg";

/**
 * The Accounting Overview empty state (new user, no accounts yet).
 *
 * Layout:
 *   - Hero fits its content (illustration + title + description + 2 actions
 *     + trust row). No forced min-height, so "How it works" is visible
 *     without a full-viewport scroll.
 *   - Floating illustration (6s cycle) replaces the book emoji.
 *   - Trust row phrasing is country-neutral so it works for every market
 *     Novala expands to.
 */

interface OverviewEmptyStateProps {
  onSetUp: () => void;
}

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  lineSoft: "#ECEFF3",
  tealDark: "#0F6E56",
};

const HOW_ANCHOR_ID = "accounting-overview-how-it-works";

export function OverviewEmptyState({ onSetUp }: OverviewEmptyStateProps) {
  const { isMobile } = useBreakpoint();

  const handleHowItWorksClick = useCallback(() => {
    document
      .getElementById(HOW_ANCHOR_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const heroStyle: CSSProperties = {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "32px 16px 40px" : "40px 24px 48px",
    textAlign: "center",
    boxSizing: "border-box",
  };

  const illusWrapStyle: CSSProperties = {
    width: "100%",
    maxWidth: isMobile ? 240 : 320,
    margin: "0 auto 20px",
    animation: "aco-float 6s ease-in-out infinite",
  };

  const illusImgStyle: CSSProperties = {
    width: "100%",
    height: "auto",
    display: "block",
  };

  const titleStyle: CSSProperties = {
    fontSize: isMobile ? 22 : 26,
    fontWeight: 700,
    margin: "0 0 8px",
    color: PALETTE.ink,
    letterSpacing: "-0.015em",
  };

  const bodyStyle: CSSProperties = {
    fontSize: isMobile ? 13 : 14.5,
    color: PALETTE.ink3,
    lineHeight: 1.6,
    margin: "0 auto 20px",
    maxWidth: 460,
  };

  const ctaRowStyle: CSSProperties = {
    display: "inline-flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "center",
  };

  const howBtnStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    background: "transparent",
    border: `1.5px solid ${PALETTE.ink}`,
    color: PALETTE.ink,
    padding: "10px 18px",
    borderRadius: 10,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  };

  return (
    <>
      <style>{`
        @keyframes aco-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-aco-illus] { animation: none !important; }
        }
      `}</style>
      <div style={{ width: "100%" }}>
        <div style={heroStyle}>
          <div style={illusWrapStyle} data-aco-illus aria-hidden="true">
            <img
              src={illustrationSrc}
              alt=""
              style={illusImgStyle}
            />
          </div>
          <h2 style={titleStyle}>Your books, kept clean</h2>
          <p style={bodyStyle}>
            Connect a bank, categorize your transactions, and reconcile your
            accounts. Every transaction gets a home. Every statement balances.
          </p>
          <div style={ctaRowStyle}>
            <button type="button" style={ctaStyle} onClick={onSetUp}>
              Set up accounting
              <ArrowRight size={16} strokeWidth={2} />
            </button>
            <button
              type="button"
              style={howBtnStyle}
              onClick={handleHowItWorksClick}
            >
              <Info size={14} strokeWidth={2} />
              How it works
            </button>
          </div>

          <div style={trustRowStyle}>
            <span style={trustItemStyle}>
              <Lock size={13} color={PALETTE.ink3} strokeWidth={2} />
              Bank-grade encryption
            </span>
            <span style={trustItemStyle}>
              <Eye size={13} color={PALETTE.ink3} strokeWidth={2} />
              Read-only access
            </span>
            <span style={trustItemStyle}>
              <Building2 size={13} color={PALETTE.ink3} strokeWidth={2} />
              Thousands of banks supported
            </span>
          </div>
        </div>

        <div id={HOW_ANCHOR_ID}>
          <HowItWorksSection />
        </div>
      </div>
    </>
  );
}

const ctaStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: PALETTE.ink,
  color: "#ffffff",
  border: 0,
  padding: "12px 24px",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  boxShadow: "0 1px 2px rgba(14, 26, 26, 0.08)",
};

const trustRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "center",
  gap: 24,
  marginTop: 28,
  fontSize: 12,
  color: PALETTE.ink3,
  fontWeight: 500,
  flexWrap: "wrap",
};

const trustItemStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};